import { useEffect, useState, useCallback } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Users, CheckCircle2, XCircle, Search, Loader2, Wand2, Layers,
  Send, FlaskConical, Trash2, Download,
} from "lucide-react";
import { toast } from "sonner";

import {
  fetchAdminWaitlist, resendWaitlistConfirmation, markWaitlistTest,
  deleteWaitlistRow, downloadWaitlistCsv,
} from "../lib/api";
import { adminApi } from "../lib/adminAuth";

/**
 * AdminWaitlist — analytics + recent-signups view for the BlendSafe
 * early-access waitlist.
 *
 * Layout mirrors the existing AdminEvents / AdminErrorLog pages so the
 * dashboard feels like one coherent surface. Top-level stat cards, then
 * a 4-up breakdown grid (country + source + UTM source + UTM medium),
 * and a recent-signups list with email search.
 */

function fmt(n) {
  return (n || 0).toLocaleString("en-US");
}

function Section({ title, eyebrow, children }) {
  return (
    <section className="bg-white rounded-2xl border border-surface-line p-5 sm:p-6">
      {eyebrow && (
        <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-ink-mute mb-2">
          {eyebrow}
        </p>
      )}
      {title && (
        <h2 className="text-[17px] font-semibold text-ink mb-4">{title}</h2>
      )}
      {children}
    </section>
  );
}

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white rounded-2xl border border-surface-line p-5 flex items-start gap-3"
    >
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-cream-100 border border-surface-line shrink-0">
        <Icon className="w-4 h-4 text-ink-soft" />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-ink-mute">{label}</p>
        <p className="mt-1 text-[26px] font-semibold text-ink tabular-nums leading-none">{value}</p>
        {sub && <p className="mt-1.5 text-[12px] text-ink-mute leading-snug">{sub}</p>}
      </div>
    </div>
  );
}

function BreakdownBar({ rows, total, labelKey, testId }) {
  if (!rows?.length) {
    return <p className="text-[13px] text-ink-mute italic">No data yet.</p>;
  }
  return (
    <ul data-testid={testId} className="space-y-2.5">
      {rows.map((row) => {
        const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
        return (
          <li key={row[labelKey]} className="flex items-center gap-3 text-[13px]">
            <span className="w-32 sm:w-40 truncate text-ink-soft capitalize">{row[labelKey]}</span>
            <span className="relative flex-1 h-1.5 rounded-full bg-cream-100 overflow-hidden">
              <span
                className="absolute inset-y-0 left-0 bg-[#7a9484] rounded-full transition-all"
                style={{ width: `${Math.max(2, pct)}%` }}
              />
            </span>
            <span className="w-16 text-right tabular-nums text-ink font-medium">{fmt(row.count)}</span>
            <span className="w-12 text-right tabular-nums text-ink-mute text-[11.5px]">{pct}%</span>
          </li>
        );
      })}
    </ul>
  );
}

function ResendBadge({ status }) {
  const s = status || "skipped";
  const map = {
    sent: "text-[#0e6b34] bg-[#0e6b34]/10 border-[#0e6b34]/20",
    failed: "text-red-600 bg-red-500/10 border-red-500/20",
    skipped: "text-ink-mute bg-cream-100 border-surface-line",
  };
  return (
    <span
      data-testid="waitlist-resend-badge"
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${map[s] || map.skipped}`}
    >
      {s}
    </span>
  );
}

export default function AdminWaitlist() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [resolveBusy, setResolveBusy] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState("");

  // Debounce email search so we don't slam the backend on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 280);
    return () => clearTimeout(t);
  }, [query]);

  const refresh = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    fetchAdminWaitlist({ q: debounced, limit: 100 })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { /* api.js interceptor handles 401 → redirect to /admin */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debounced]);

  useEffect(() => refresh(), [refresh]);

  async function resolveUnknown() {
    if (resolveBusy) return;
    setResolveBusy(true);
    try {
      const { data: r } = await adminApi().post(
        "/admin/v2/waitlist/resolve-unknown-countries",
      );
      const msg = `Resolved ${r.resolved} of ${r.scanned} unknown-country rows`;
      (r.resolved > 0 ? toast.success : toast.message)(msg, {
        description: r.still_unknown
          ? `${r.still_unknown} could not be resolved (no IP / private network)`
          : undefined,
      });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Resolve failed");
    } finally {
      setResolveBusy(false);
    }
  }

  async function backfillTiers() {
    if (backfillBusy) return;
    setBackfillBusy(true);
    try {
      const { data: r } = await adminApi().post(
        "/admin/v2/waitlist/backfill-tiers",
      );
      toast.success(`Tier backfill complete`, {
        description: `${r.updated} rows updated · ${r.skipped_already_tiered} already tiered`,
      });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Backfill failed");
    } finally {
      setBackfillBusy(false);
    }
  }

  const total = data?.total ?? 0;
  const verified = data?.verified ?? 0;
  const unverified = data?.unverified ?? Math.max(0, total - verified);
  const genuine = data?.genuine_founding_count ?? 0;
  const byCountry = data?.by_country || [];

  async function doResend(email) {
    setRowBusy(`${email}:resend`);
    try {
      const r = await resendWaitlistConfirmation(email);
      (r.status === "sent" ? toast.success : toast.message)(`Resend: ${r.status}`, {
        description: r.resend_id ? `Resend id ${r.resend_id}` : (r.reason || ""),
      });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally { setRowBusy(""); }
  }
  async function doMarkTest(email) {
    if (!window.confirm(`Mark ${email} as a test row? It drops from the public count.`)) return;
    setRowBusy(`${email}:test`);
    try { await markWaitlistTest(email); toast.success("Marked as test"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRowBusy(""); }
  }
  async function doDelete(email) {
    if (!window.confirm(`Delete ${email}? This cannot be undone.`)) return;
    setRowBusy(`${email}:del`);
    try { await deleteWaitlistRow(email); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRowBusy(""); }
  }
  async function doExport() {
    try {
      const blob = await downloadWaitlistCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "founding-members.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  }

  const bySource = data?.by_source || [];
  const byUtmSource = data?.by_utm_source || [];
  const byUtmMedium = data?.by_utm_medium || [];
  const byTier = data?.by_tier || [];
  const recent = data?.recent || [];
  const migration = data?.migration || {};
  const topCountry = byCountry[0];
  const topSource = bySource[0];

  return (
    <main
      data-testid="admin-waitlist-page"
      className="min-h-screen bg-cream-50 px-4 sm:px-6 lg:px-10 pt-28 pb-24"
    >
      <div className="max-w-6xl mx-auto">
        <AdminHeader />
        <header className="mb-7 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-ink-mute mb-2">
              Growth · Founding members
            </p>
            <h1 className="font-luxe text-[34px] sm:text-[44px] leading-[1.05] tracking-tight text-ink">
              Founding members
            </h1>
            <p className="mt-2 text-[14px] text-ink-soft max-w-xl">
              Everyone who claimed a founding spot on blendsafe.com. Only
              email-confirmed rows count toward the public number.
            </p>
          </div>
          <button
            type="button"
            onClick={doExport}
            data-testid="waitlist-export-csv"
            className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3.5 py-2 text-xs font-semibold text-stone-900 hover:border-stone-900 shrink-0"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </header>

        {/* Counters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-6">
          <StatCard testId="stat-total" icon={Users} label="Total"
            value={fmt(total)} sub={`${fmt(genuine)} count publicly (verified, non-test)`} />
          <StatCard testId="stat-verified" icon={CheckCircle2} label="Verified"
            value={fmt(verified)} sub="Confirmed their email" />
          <StatCard testId="stat-unverified" icon={XCircle} label="Unverified"
            value={fmt(unverified)} sub="Haven't clicked confirm yet" />
        </div>

        {/* Migration card — shown only when data is stale */}
        {(migration.unknown_country_count > 0 || migration.missing_tier_count > 0) && (
          <Section
            eyebrow="Data quality"
            title="Run migrations to backfill legacy rows"
          >
            <div
              data-testid="waitlist-migration-card"
              className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between"
            >
              <div className="text-[13px] text-ink-soft">
                {migration.unknown_country_count > 0 && (
                  <p data-testid="unknown-country-summary">
                    <strong className="text-ink">{fmt(migration.unknown_country_count)}</strong> rows have an unknown country — most can be resolved by re-running the IP lookup.
                  </p>
                )}
                {migration.missing_tier_count > 0 && (
                  <p data-testid="missing-tier-summary">
                    <strong className="text-ink">{fmt(migration.missing_tier_count)}</strong> legacy rows are missing a tier and need to be backfilled.
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {migration.unknown_country_count > 0 && (
                  <button
                    type="button"
                    onClick={resolveUnknown}
                    disabled={resolveBusy}
                    data-testid="resolve-unknown-countries-button"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3.5 py-2 text-xs font-semibold text-stone-900 hover:border-stone-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {resolveBusy
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Wand2 className="w-3.5 h-3.5" />}
                    Resolve unknown countries
                  </button>
                )}
                {migration.missing_tier_count > 0 && (
                  <button
                    type="button"
                    onClick={backfillTiers}
                    disabled={backfillBusy}
                    data-testid="backfill-tiers-button"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3.5 py-2 text-xs font-semibold text-stone-900 hover:border-stone-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {backfillBusy
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Layers className="w-3.5 h-3.5" />}
                    Backfill tiers
                  </button>
                )}
              </div>
            </div>
          </Section>
        )}

        {/* Tier breakdown — small pill row above the main breakdowns */}
        {byTier.length > 0 && (
          <div
            data-testid="tier-breakdown"
            className="mt-6 flex flex-wrap gap-2"
          >
            {byTier.map((t) => (
              <span
                key={t.tier}
                data-testid={`tier-pill-${t.tier}`}
                className="inline-flex items-center gap-2 rounded-full bg-white border border-surface-line px-3 py-1.5 text-[12px] text-ink-soft"
              >
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-mute">
                  {t.tier}
                </span>
                <span className="font-semibold text-ink tabular-nums">{fmt(t.count)}</span>
              </span>
            ))}
          </div>
        )}

        {/* Breakdowns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 mt-6 mb-6">
          <Section eyebrow="By country" title="Where they're signing up from">
            <BreakdownBar
              testId="breakdown-country"
              rows={byCountry}
              total={total}
              labelKey="country"
            />
          </Section>
          <Section eyebrow="By source" title="How they're discovering BlendSafe">
            <BreakdownBar
              testId="breakdown-source"
              rows={bySource}
              total={total}
              labelKey="source"
            />
          </Section>
          <Section eyebrow="UTM source" title="Campaigns driving signups">
            <BreakdownBar
              testId="breakdown-utm-source"
              rows={byUtmSource}
              total={total}
              labelKey="utm_source"
            />
          </Section>
          <Section eyebrow="UTM medium" title="Channels driving signups">
            <BreakdownBar
              testId="breakdown-utm-medium"
              rows={byUtmMedium}
              total={total}
              labelKey="utm_medium"
            />
          </Section>
        </div>

        {/* Recent signups + search */}
        <Section eyebrow="Recent" title="Latest signups">
          <div className="relative mb-4 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-light pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by email…"
              data-testid="admin-waitlist-search"
              className="w-full h-10 pl-10 pr-4 rounded-xl border border-surface-line bg-cream-50 text-[13.5px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-cream-100 focus:border-ink-mute"
            />
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-ink-mute text-[13px] py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading signups…
            </div>
          ) : recent.length === 0 ? (
            <p className="text-[13px] text-ink-mute italic py-2">No signups match.</p>
          ) : (
            <div
              data-testid="admin-waitlist-recent"
              className="rounded-xl border border-surface-line overflow-x-auto"
            >
              <div className="min-w-[1040px]">
                <div className="grid grid-cols-[110px_minmax(190px,1fr)_140px_92px_112px_120px_100px_120px] px-4 py-2.5 bg-cream-100/60 border-b border-surface-line text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-mute">
                  <span>First name</span>
                  <span>Email</span>
                  <span>Joined</span>
                  <span>Verified</span>
                  <span>Resend</span>
                  <span>Country</span>
                  <span>Source</span>
                  <span className="text-right">Actions</span>
                </div>
                <ul className="divide-y divide-surface-line">
                  {recent.map((r) => {
                    const busy = (a) => rowBusy === `${r.email}:${a}`;
                    return (
                      <li
                        key={`${r.email}-${r.created_at}`}
                        data-testid="admin-waitlist-row"
                        className="grid grid-cols-[110px_minmax(190px,1fr)_140px_92px_112px_120px_100px_120px] items-center gap-3 px-4 py-3 text-[13px]"
                      >
                        <span className="text-ink-soft truncate">{r.first_name || "—"}</span>
                        <span className="font-medium text-ink truncate" title={r.email}>{r.email}</span>
                        <span className="text-ink-mute tabular-nums text-[12px]">
                          {(r.created_at || "").slice(0, 16).replace("T", " · ") || "—"}
                        </span>
                        <span>
                          {r.verified ? (
                            <span data-testid="waitlist-verified-yes" className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#0e6b34]">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Yes
                            </span>
                          ) : (
                            <span data-testid="waitlist-verified-pending" className="inline-flex items-center gap-1 text-[11.5px] text-ink-mute">
                              <XCircle className="w-3.5 h-3.5" /> Pending
                            </span>
                          )}
                        </span>
                        <span><ResendBadge status={r.resend_status} /></span>
                        <span className="text-ink-soft truncate">{r.country}</span>
                        <span className="text-ink-soft capitalize truncate">{r.source}</span>
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => doResend(r.email)}
                            disabled={busy("resend")}
                            title="Resend confirmation email"
                            data-testid={`waitlist-action-resend-${r.email}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-surface-line text-ink-soft hover:border-ink/40 hover:text-ink transition-colors disabled:opacity-50"
                          >
                            {busy("resend") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => doMarkTest(r.email)}
                            disabled={busy("test")}
                            title="Mark as test (drops from public count)"
                            data-testid={`waitlist-action-marktest-${r.email}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-surface-line text-ink-soft hover:border-amber-500/60 hover:text-amber-600 transition-colors disabled:opacity-50"
                          >
                            {busy("test") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => doDelete(r.email)}
                            disabled={busy("del")}
                            title="Delete row (cannot be undone)"
                            data-testid={`waitlist-action-delete-${r.email}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-surface-line text-ink-soft hover:border-red-400 hover:text-red-600 transition-colors disabled:opacity-50"
                          >
                            {busy("del") ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </Section>
      </div>
    </main>
  );
}
